/**
 * Iter 230 · AirQ Super-Admin dashboard
 * Live KPIs + Leaflet heatmap of origin points for aeromedical missions.
 * Superadmin/admin only (backend enforces).
 */
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { AirplaneTilt, Flame, Warning, ShieldCheck } from "@phosphor-icons/react";

const RANGES = [
  { key: 7,  label: "7d"  },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

export default function AirqSuperAdmin() {
  const [days, setDays] = useState(30);
  const [kpi, setKpi] = useState(null);
  const [pts, setPts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [k, h] = await Promise.all([
          api.get(`/wave4/airq/superadmin/kpis?days=${days}`),
          api.get(`/wave4/airq/superadmin/heatmap?days=${days}`),
        ]);
        setKpi(k.data); setPts(h.data.points || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [days]);

  const center = useMemo(() => {
    if (!pts.length) return [10.85, 76.27];  // Kerala default
    const lat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
    const lng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
    return [lat, lng];
  }, [pts]);

  const successRate = kpi?.success_rate_pct;

  return (
    <AppShell title="AirQ · Super-Admin" subtitle="Founder-grade view · live KPIs + mission heatmap">
      <div className="space-y-4" data-testid="airq-superadmin-root">
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setDays(r.key)}
                     data-testid={`airq-sa-range-${r.key}`}
                     className={`px-3 py-1.5 text-xs rounded ${
                       days===r.key ? "bg-[#0F766E] text-white"
                                        : "bg-white border text-[#5C5A55]"}`}>
              {r.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Missions" v={kpi?.missions_total ?? "—"}   c="#0F766E" icon={AirplaneTilt}/>
          <KpiCard label="Success rate"
                    v={successRate != null ? `${successRate}%` : "—"} c="#166534" icon={ShieldCheck}/>
          <KpiCard label="Escalated" v={kpi?.escalated_missions ?? "—"} c="#B91C1C" icon={Flame}/>
          <KpiCard label="Compliance ⚠︎ 30d"
                    v={kpi?.compliance_docs_expiring_30d ?? "—"} c="#B45309" icon={Warning}/>
          <KpiCard label="Aircraft" v={`${kpi?.aircraft_in_flight ?? 0}/${kpi?.aircraft_total ?? 0}`} c="#4338CA"/>
          <KpiCard label="Missions completed" v={kpi?.missions_completed ?? "—"} c="#059669"/>
          <KpiCard label="Pad approvals ⏳" v={kpi?.pending_pad_approvals ?? "—"} c="#0369A1"/>
          <KpiCard label="Origin heatmap pts" v={pts.length} c="#7C3AED"/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg border p-4 md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
              Mission heatmap · origin points (weight = P0>P1>P2)
            </div>
            <div className="h-[380px] rounded overflow-hidden" data-testid="airq-sa-map">
              <MapContainer center={center} zoom={7} style={{height: "100%", width: "100%"}}
                              scrollWheelZoom={false}>
                <TileLayer attribution="&copy; OpenStreetMap"
                             url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                {pts.map((p, i) => (
                  <CircleMarker key={i} center={[p.lat, p.lng]}
                                  radius={4 + p.weight * 2}
                                  pathOptions={{
                                    color: p.status==="completed" ? "#166534"
                                          : p.status==="dispatched" ? "#B45309"
                                          : "#B91C1C",
                                    fillOpacity: 0.5,
                                  }}>
                    <Popup>
                      <div className="text-xs">
                        <b>{p.kind || "mission"}</b><br/>
                        {p.status} · weight {p.weight}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          </div>
          <div className="space-y-3">
            <BreakdownCard title="By status" data={kpi?.missions_by_status}/>
            <BreakdownCard title="By kind"   data={kpi?.missions_by_kind}/>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KpiCard({ label, v, c, icon: Icon }) {
  return (
    <div className="p-4 bg-white rounded-lg border" data-testid={`airq-sa-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-[22px] font-heading font-semibold" style={{color:c}}>{v}</div>
        {Icon && <Icon size={20} style={{color: c, opacity: 0.35}}/>}
      </div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function BreakdownCard({ title, data }) {
  const rows = Object.entries(data || {});
  const total = rows.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No data</div>
        : rows.map(([k, v]) => (
          <div key={k} className="mb-1.5">
            <div className="flex justify-between text-xs mb-0.5">
              <span className="capitalize">{k.replace("_"," ")}</span>
              <span className="text-[#8A8780]">{v}</span>
            </div>
            <div className="h-1.5 bg-[#F5F3EE] rounded">
              <div className="h-full rounded bg-[#0F766E]"
                     style={{width: `${(v/total)*100}%`}}/>
            </div>
          </div>
        ))}
    </div>
  );
}
